import { useCallback, useMemo } from 'react';
import type { MutationOptions } from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type {
  ApiDocument,
  ApiDocumentCategory,
  ApiError,
  ApiRequestBodyType,
  ApiRequestQueryParamType,
  DocumentsApi,
  GetDocumentsQueryParams,
} from '@izimi/api';
import type { FirstParameter, WithRequired } from '@izimi/utils';
import { chunkArray, nonNullable } from '@izimi/utils';

import {
  useInvalidateSharedItemsCache,
  useOptimisticSharedItemsDocumentAcceptRejectCacheUpdate,
} from '../shared';
import { useInvalidateVaultInfoCache, useInvalidateVaultPreferencesCache } from '../vault';
import {
  documentsQueryKeys,
  useInvalidateDocumentCache,
  useInvalidateDocumentsCache,
  useInvalidateDocumentsPreviewCache,
  useInvalidateDocumentsSearchCache,
  useInvalidateDocumentsSharedByYouListCache,
  useInvalidateDocumentsSharedFromNotaryListCache,
  useInvalidateDocumentsSharedToYouListCache,
  useInvalidateDocumentsTrashListCache,
  useInvalidateDocumentsUnSeenListCache,
  useInvalidateDocumentsVaultListCache,
  useInvalidateDocumentsVaultTransferCache,
  useSetDocumentCache,
  useUpdatePagedDocumentsCache,
} from './cache';
import type DocumentEntity from './DocumentEntity';
import { mapDocumentsToEntities, mapDocumentToEntity } from './helpers';
import type { PagedDocumentsArgs } from './types';

const STALE_30_MINUTES = 1800000;
const POLLING_RATE = 2500;
const DOCUMENTS_FETCH_CHUNK_SIZE = 100;

const createDocumentsModule = ({ documentsApi }: { documentsApi: DocumentsApi }) => {
  const useDocumentFilters = (params?: PagedDocumentsArgs['params']) => {
    const { data, isLoading, error } = useQuery({
      queryKey: documentsQueryKeys.getFilters(),
      queryFn: () =>
        documentsApi
          .getDocuments({
            limit: 1,
            offset: 0,
            returnUniqueFilterValues: true,
            ...params,
          })
          .then((res) => res.uniqueFilterValues),
    });

    return {
      documentFilters: data,
      isLoading,
      error,
    };
  };

  const usePagedDocuments = ({
    enabled = true,
    params,
    type,
    pageSize = 15,
  }: WithRequired<PagedDocumentsArgs, 'type'>) => {
    const sortParam = params?.sort ?? 'name,asc';

    const { data, isLoading, error, refetch, fetchNextPage, isFetchingNextPage, hasNextPage } =
      useInfiniteQuery<Awaited<ReturnType<typeof documentsApi.getDocuments>>, ApiError>({
        queryKey: documentsQueryKeys.getDocuments(type, {
          ...params,
          sort: sortParam,
          limit: pageSize,
        }),
        initialPageParam: 0,
        queryFn: ({ pageParam }) =>
          documentsApi.getDocuments({
            ...params,
            offset: Number(pageParam),
            limit: pageSize,
            sort: sortParam,
          }),
        enabled,
        staleTime: type === 'documentNameMatch' ? 0 : STALE_30_MINUTES,
        getNextPageParam: (lastPage, allPages) => {
          const totalFetchedDocuments = allPages.flatMap((page) => page.documents).length;

          return totalFetchedDocuments < lastPage.total ? allPages.length * pageSize : undefined;
        },
      });

    const allDocuments = useMemo(
      () => mapDocumentsToEntities(data?.pages.flatMap((page) => page.documents)),
      [data?.pages],
    );

    return {
      documents: allDocuments,
      total: data?.pages[0]?.total ?? 0,
      uniqueFilterValues: data?.pages[0]?.uniqueFilterValues,
      totalUnseen: data?.pages[0]?.numberUnseenDocuments,
      error,
      isLoading,
      refetch,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
    };
  };

  const useDocument = ({
    documentId,
    enabled = true,
    pollUntilScanned = false,
  }: {
    documentId?: string;
    enabled?: boolean;
    pollUntilScanned?: boolean;
  }) => {
    const {
      data: document,
      isLoading,
      error,
      refetch,
    } = useQuery({
      queryKey: documentsQueryKeys.getDocument(documentId!),
      queryFn: () =>
        documentsApi.getDocument({ documentId: documentId! }).then(mapDocumentToEntity),
      enabled: enabled && Boolean(documentId),
      refetchInterval: (query) =>
        pollUntilScanned && query.state.data?.isBeingScanned === true ? POLLING_RATE : false,
    });

    return {
      document,
      error,
      isLoading,
      refetch,
    };
  };

  const useGetDocuments = ({
    ids,
    ...params
  }: Omit<GetDocumentsQueryParams, 'limit' | 'offset'> &
    Required<Pick<GetDocumentsQueryParams, 'ids'>>) => {
    const documentIdsChunks = chunkArray(ids, DOCUMENTS_FETCH_CHUNK_SIZE);
    const result = useQueries({
      queries: documentIdsChunks.map((documentIdsChunk) => ({
        queryKey: documentsQueryKeys.getDocuments('selectDocuments', { ids: documentIdsChunk }),
        queryFn: () =>
          documentsApi
            .getDocuments({ ids: documentIdsChunk, ...params, limit: DOCUMENTS_FETCH_CHUNK_SIZE })
            .then((res) => res.documents.map(mapDocumentToEntity)),
      })),
    });

    return {
      documents: result
        .map((r) => r.data)
        .filter(nonNullable)
        .flat(),
      isError: result.some((r) => r.isError),
      isLoading: result.some((r) => r.isLoading),
    };
  };

  const useGetDocumentsScanning = ({ ids }: { ids: string[] }) => {
    const {
      data: documents,
      isLoading,
      error,
    } = useQuery({
      queryKey: documentsQueryKeys.getDocuments('scanning'),
      queryFn: () =>
        documentsApi.getDocuments({ ids }).then((res) => mapDocumentsToEntities(res.documents)),

      refetchInterval: (query) => {
        if (query.state.data?.some((doc) => doc.isBeingScanned)) {
          return POLLING_RATE;
        }
        return false;
      },
    });

    return {
      documents,
      isScanning: documents?.some((doc) => doc.isBeingScanned),
      isLoading,
      error,
    };
  };

  const useMoveDocumentsToTrash = () => {
    const queryClient = useQueryClient();
    const invalidateVaultInfoCache = useInvalidateVaultInfoCache();
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();
    const invalidateDocumentsVaultTransferCache = useInvalidateDocumentsVaultTransferCache();
    const invalidateVaultPreferencesCache = useInvalidateVaultPreferencesCache();
    const invalidateDocumentsUnSeenListCache = useInvalidateDocumentsUnSeenListCache();
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
    const invalidateDocumentsTrashListCache = useInvalidateDocumentsTrashListCache();
    const invalidateDocumentsSharedByYouListCache = useInvalidateDocumentsSharedByYouListCache();
    const invalidateDocumentSharedToYouListCache = useInvalidateDocumentsSharedToYouListCache();
    const invalidateSharedItemsCache = useInvalidateSharedItemsCache();

    const { mutateAsync, isPending, error } = useMutation<ApiDocument[], ApiError, string[]>({
      mutationFn: (documentIds) => documentsApi.moveDocumentsToTrash(documentIds),
      onSuccess(trashDocuments, documentIds) {
        updatePagedDocumentsCache('vaultList', (allDocuments) => {
          return allDocuments.filter((doc) => !documentIds.includes(doc.id));
        });

        updatePagedDocumentsCache('trashList', (allDocuments) => {
          return [...trashDocuments, ...allDocuments];
        });

        // trashing documents causes the vault sizes to change
        invalidateVaultInfoCache();
        invalidateDocumentsVaultListCache();
        invalidateDocumentsTrashListCache();
        invalidateDocumentsUnSeenListCache();
        invalidateDocumentsSharedByYouListCache();
        invalidateDocumentSharedToYouListCache();
        invalidateSharedItemsCache();

        // trashing documents can cause changes in vault transfer preferences
        invalidateDocumentsVaultTransferCache();
        invalidateVaultPreferencesCache();

        // Update individual document cache
        trashDocuments.forEach((trashDocument) =>
          queryClient.removeQueries({
            queryKey: documentsQueryKeys.getDocument(trashDocument.id),
            exact: true,
          }),
        );
      },
    });

    return {
      moveDocumentsToTrash: mutateAsync,
      error,
      isPending,
    };
  };

  const usePermanentlyDeleteDocuments = () => {
    const invalidateVaultInfoCache = useInvalidateVaultInfoCache();
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();
    const invalidateTrashListCache = useInvalidateDocumentsTrashListCache();

    const { mutateAsync, isPending, error } = useMutation<
      void,
      ApiError,
      {
        documentIds: string[];
      } & ApiRequestQueryParamType<'deleteDocuments'>
    >({
      mutationFn: ({ documentIds, forceDelete = false }) =>
        documentsApi.permanentlyDeleteDocuments(documentIds, { forceDelete }),
      onSuccess: (_, { documentIds }) => {
        updatePagedDocumentsCache('trashList', (allDocuments) => {
          return allDocuments.filter((doc) => !documentIds.includes(doc.id));
        });

        // deleting documents causes the vault sizes to change
        invalidateVaultInfoCache();

        // Deleting documents might need a refresh of the trash list
        invalidateTrashListCache();
      },
    });

    return {
      permanentlyDeleteDocuments: mutateAsync,
      error,
      isPending,
    };
  };

  const useEmptyTrash = () => {
    const invalidateVaultInfoCache = useInvalidateVaultInfoCache();
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();

    const { mutateAsync, isPending, error } = useMutation<void, ApiError>({
      mutationFn: () =>
        documentsApi.permanentlyDeleteDocuments([], {
          deleteAll: true,
        }),
      onSuccess: () => {
        updatePagedDocumentsCache('trashList', () => []);
        // deleting documents causes the vault sizes to change
        invalidateVaultInfoCache();
      },
    });

    return {
      emptyTrash: mutateAsync,
      error,
      isPending,
    };
  };

  const useUpdateDocument = ({
    onSuccess,
  }: {
    onSuccess?: (document: ApiDocument) => void;
  } = {}) => {
    const queryClient = useQueryClient();
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();
    const setDocumentCache = useSetDocumentCache();

    const { mutateAsync, isPending, error } = useMutation<
      ApiDocument,
      ApiError,
      { documentId: string; values: Partial<ApiDocument> },
      { previousDocument: DocumentEntity; previousVaultList: any }
    >({
      mutationFn: ({ documentId, values }) => documentsApi.updateDocument({ documentId }, values),
      onMutate: async ({ documentId, values }) => {
        const documentQueryKey = documentsQueryKeys.getDocument(documentId);
        // Cancel any outgoing refetch
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: documentQueryKey,
        });
        // Snapshot the previous value
        const previousVaultList = queryClient.getQueryData(['vaultList']);
        // Check if the document exists in cache (which might not be the case if sidebar wasn't opened)
        const document = queryClient.getQueryData<DocumentEntity>(documentQueryKey);

        // different memory reference so it will not be mutated
        const previousDocument = document
          ? Object.assign(Object.create(Object.getPrototypeOf(document)), document)
          : undefined;

        if (document) {
          // only update the modification date if the name has changed (backend requirement)
          const shouldUpdateModificationDate = previousDocument?.name !== values.name;

          // Optimistically updated document entity
          const updatedDocument = Object.assign(document, values, {
            modificationDate: shouldUpdateModificationDate
              ? new Date().toUTCString()
              : document.modificationDate,
          });
          // Optimistically update to the new value
          queryClient.setQueryData<DocumentEntity>(documentQueryKey, () => updatedDocument);
          // Optimistically update the vault list cache
          updatePagedDocumentsCache('vaultList', (allDocuments) => {
            return allDocuments.map((doc) => {
              if (doc.id === documentId) {
                return {
                  ...doc,
                  ...values,
                  modificationDate: shouldUpdateModificationDate
                    ? new Date().toUTCString()
                    : doc.modificationDate,
                };
              }
              return doc;
            });
          });
        }
        // Return a context object with the snapshot value
        return { previousDocument, previousVaultList };
      },
      onError: (_, { documentId }, context) => {
        const documentQueryKey = documentsQueryKeys.getDocument(documentId);
        if (context?.previousDocument) {
          queryClient.setQueryData(documentQueryKey, context.previousDocument);
        }
        if (context?.previousVaultList) {
          queryClient.setQueryData(['vaultList'], context.previousVaultList);
        }
      },
      onSuccess(document, { documentId }) {
        setDocumentCache(mapDocumentToEntity(document));
        if (onSuccess) {
          onSuccess(document);
        } else {
          updatePagedDocumentsCache('vaultList', (allDocuments) => {
            return allDocuments.map((doc) => {
              if (doc.id === documentId) {
                return document;
              }
              return doc;
            });
          });
        }
      },
    });

    return {
      updateDocument: mutateAsync,
      error,
      isPending,
    };
  };

  const useUpdateDocumentCategories = (document: DocumentEntity) => {
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
    const { updateDocument, error, isPending } = useUpdateDocument({
      onSuccess: invalidateDocumentsVaultListCache,
    });
    const updateDocumentCategories = useCallback(
      (categories: ApiDocumentCategory[]) =>
        updateDocument({
          documentId: document.id,
          values: {
            id: document.id,
            name: document.name,
            labels: document.labels,
            categories,
          },
        }),
      [updateDocument, document],
    );

    return {
      updateDocumentCategories,
      error,
      isPending,
    };
  };

  const useUpdateDocumentFileName = (document: DocumentEntity) => {
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
    const invalidateDocumentsPreviewCache = useInvalidateDocumentsPreviewCache();
    const invalidateSearchDocumentsCache = useInvalidateDocumentsSearchCache();
    const invalidateSharedItemsCache = useInvalidateSharedItemsCache();
    const { updateDocument, error, isPending } = useUpdateDocument({
      onSuccess: () => {
        invalidateSharedItemsCache();
        invalidateDocumentsVaultListCache();
        invalidateSearchDocumentsCache();
        invalidateDocumentsPreviewCache();
      },
    });
    const updateDocumentFileName = useCallback(
      (fileName: string) =>
        updateDocument({
          documentId: document.id,
          values: {
            id: document.id,
            name: fileName,
            labels: document.labels,
            categories: document.categories,
          },
        }),
      [updateDocument, document],
    );

    return {
      updateDocumentFileName,
      error,
      isPending,
    };
  };

  const useUpdateDocumentLabels = (document: DocumentEntity) => {
    const { updateDocument, error, isPending } = useUpdateDocument();
    const updateDocumentLabels = useCallback(
      (labels: string[]) =>
        updateDocument({
          documentId: document.id,
          values: {
            id: document.id,
            name: document.name,
            labels,
            categories: document.categories,
          },
        }),
      [updateDocument, document],
    );

    return {
      updateDocumentLabels,
      error,
      isPending,
    };
  };

  const useMarkDocumentAsSeen = () => {
    const setDocumentCache = useSetDocumentCache();
    const invalidateDocumentsUnSeenListCache = useInvalidateDocumentsUnSeenListCache();
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();

    const { mutateAsync, isPending, error } = useMutation<
      Array<ApiDocument>,
      ApiError,
      {
        documentIds: ApiRequestBodyType<'markDocumentsAsSeen'>;
        isSeen: boolean;
      }
    >({
      mutationFn: ({ documentIds, isSeen }) => documentsApi.markDocumentsSeen(documentIds, isSeen),
      onSuccess(documents) {
        invalidateDocumentsVaultListCache();
        invalidateDocumentsUnSeenListCache();
        mapDocumentsToEntities(documents).forEach((d) => setDocumentCache(d));
      },
    });

    return {
      markDocumentAsSeen: mutateAsync,
      error,
      isPending,
    };
  };

  const useCopyMultipleInheritedDocuments = () => {
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();
    const { copyDocument, isPending } = useCopyDocument();

    const copyMultipleInheritedDocuments = async (documentIds: string[]) => {
      await Promise.all(documentIds.map((documentId: string) => copyDocument(documentId)));
      updatePagedDocumentsCache('inheritedList', (allDocuments) =>
        allDocuments.map((d) => (documentIds.includes(d.id) ? { ...d, isCopied: true } : d)),
      );
    };

    return {
      isPending,
      copyMultipleInheritedDocuments,
    };
  };

  const useCopyInheritedDocument = () => {
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();

    const { copyDocument, isPending } = useCopyDocument({
      onSuccess(_, documentId) {
        updatePagedDocumentsCache('inheritedList', (allDocuments) =>
          allDocuments.map((d) => (d.id === documentId ? { ...d, isCopied: true } : d)),
        );
      },
    });

    return {
      isPending,
      copyInheritedDocument: copyDocument,
    };
  };

  const useCopyDocument = ({
    onSuccess,
  }: {
    onSuccess?: MutationOptions<ApiDocument, ApiError, string>['onSuccess'];
  } = {}) => {
    const invalidateVaultInfoCache = useInvalidateVaultInfoCache();
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();

    const { mutateAsync, isPending, error } = useMutation<ApiDocument, ApiError, string>({
      mutationFn: (documentId) => documentsApi.copyDocument({ documentId }),
      onSuccess(...args) {
        invalidateDocumentsVaultListCache();
        // copy documents causes the vault sizes to change
        invalidateVaultInfoCache();

        onSuccess?.(...args);
      },
    });

    return {
      copyDocument: mutateAsync,
      error,
      isPending,
    };
  };

  const useShareDocuments = () => {
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();
    const invalidateDocumentsSharedByYouListCache = useInvalidateDocumentsSharedByYouListCache();
    const invalidateDocumentsCache = useInvalidateDocumentsCache();

    const { mutateAsync, isPending, error } = useMutation<
      ApiDocument[],
      ApiError,
      FirstParameter<typeof documentsApi.shareDocuments>
    >({
      mutationFn: (values) => documentsApi.shareDocuments(values),
      onSuccess(documents) {
        updatePagedDocumentsCache('vaultList', (allDocuments) => {
          return allDocuments.map((document) => {
            const newDoc = documents.find((doc) => doc.id === document.id);
            return newDoc ?? document;
          });
        });
        invalidateDocumentsSharedByYouListCache();
        invalidateDocumentsCache(documents.map(({ id }) => id));
      },
    });

    return {
      shareDocuments: mutateAsync,
      error,
      isPending,
    };
  };

  const useUnShareDocuments = () => {
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();
    const setDocumentCache = useSetDocumentCache();
    const invalidateDocumentsSharedByYouListCache = useInvalidateDocumentsSharedByYouListCache();

    const { mutateAsync, isPending, error } = useMutation<
      ApiDocument[],
      ApiError,
      FirstParameter<typeof documentsApi.unshareDocuments>
    >({
      mutationFn: (values) => documentsApi.unshareDocuments(values),
      onSuccess(documents) {
        updatePagedDocumentsCache('vaultList', (allDocuments) => {
          return allDocuments.map((document) => {
            const newDoc = documents.find((doc) => doc.id === document.id);
            return newDoc ?? document;
          });
        });
        invalidateDocumentsSharedByYouListCache();
        documents.forEach((document) => {
          setDocumentCache(mapDocumentToEntity(document));
        });
      },
    });

    return {
      unShareDocuments: mutateAsync,
      error,
      isPending,
    };
  };

  const useUnTrashDocuments = () => {
    const invalidateVaultInfoCache = useInvalidateVaultInfoCache();
    const invalidateDocumentsUnSeenListCache = useInvalidateDocumentsUnSeenListCache();
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
    const invalidateDocumentsSharedByYouListCache = useInvalidateDocumentsSharedByYouListCache();
    const invalidateDocumentsSharedToYouListCache = useInvalidateDocumentsSharedToYouListCache();
    const invalidateDocumentsTrashListCache = useInvalidateDocumentsTrashListCache();
    const invalidateSharedItemsCache = useInvalidateSharedItemsCache();

    const { mutateAsync, isPending, error } = useMutation<ApiDocument[], ApiError, string[]>({
      mutationFn: (documentIds) => documentsApi.unTrashDocuments(documentIds),
      onSuccess() {
        invalidateVaultInfoCache();
        invalidateDocumentsVaultListCache();
        invalidateDocumentsTrashListCache();
        invalidateDocumentsUnSeenListCache();
        invalidateDocumentsSharedByYouListCache();
        invalidateDocumentsSharedToYouListCache();
        invalidateSharedItemsCache();
      },
    });

    return {
      unTrashDocuments: mutateAsync,
      error,
      isPending,
    };
  };

  const useTrashDocumentIds = (total: number) => {
    const { documents: documentsToTrash, isLoading } = usePagedDocuments({
      params: {
        states: 'TRASHED',
      },
      type: 'trashList',
      pageSize: total,
    });

    return {
      trashDocumentIds: documentsToTrash.map((doc) => doc.id),
      isLoading,
    };
  };

  const useAcceptRejectSharedDocuments = () => {
    const { onMutate, onError } = useOptimisticSharedItemsDocumentAcceptRejectCacheUpdate();
    const invalidateSharedItemsCache = useInvalidateSharedItemsCache();
    const invalidateDocumentsSharedToYouListCache = useInvalidateDocumentsSharedToYouListCache();
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
    const invalidateDocumentsSharedFromNotaryListCache =
      useInvalidateDocumentsSharedFromNotaryListCache();
    const invalidateDocumentCache = useInvalidateDocumentCache();
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();
    const invalidateDocumentsUnSeenListCache = useInvalidateDocumentsUnSeenListCache();

    const { mutateAsync, isPending, error } = useMutation({
      mutationFn: (values) => documentsApi.acceptRejectSharedDocuments(values),
      onMutate,
      onError,
      onSuccess({ acceptedDocuments, rejectedDocuments }) {
        const acceptedIds = acceptedDocuments.map((d) => d.id);
        const rejectedIds = rejectedDocuments.map((d) => d.documentId);
        const ids = [...acceptedIds, ...rejectedIds];
        if (ids.length > 0) {
          ids.forEach(invalidateDocumentCache);
        }
        invalidateSharedItemsCache();
        invalidateDocumentsVaultListCache();
        invalidateDocumentsSharedToYouListCache();
        invalidateDocumentsSharedFromNotaryListCache();
        invalidateDocumentsUnSeenListCache();
        updatePagedDocumentsCache('sharedFromNotaryList', (allDocuments) => {
          return allDocuments.filter((doc) => {
            return !ids.includes(doc.id);
          });
        });
      },
    });

    return {
      acceptRejectSharedDocuments: mutateAsync,
      error,
      isPending,
    };
  };

  const useDocumentLabels = ({ enabled = true } = {}) => {
    const { data, isLoading, error, refetch, isSuccess } = useQuery({
      queryKey: documentsQueryKeys.getLabels(),
      queryFn: () => documentsApi.getLabels(),

      enabled,
    });

    return {
      labels: data,
      error,
      isLoading,
      refetch,
      isSuccess,
    };
  };

  const useDocumentExtendTrashDate = () => {
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
    const setDocumentCache = useSetDocumentCache();

    const { mutateAsync, isPending, error } = useMutation<
      Array<ApiDocument>,
      ApiError,
      Array<ApiDocument['id']>
    >({
      mutationFn: (values) => documentsApi.documentExtendTrashDate(values),
      onSuccess: (documents) => {
        invalidateDocumentsVaultListCache();
        mapDocumentsToEntities(documents).forEach((d) => setDocumentCache(d));
      },
    });

    return {
      documentExtendTrashDate: mutateAsync,
      error,
      isPending,
    };
  };

  return {
    useDocumentFilters,
    usePagedDocuments,
    useDocument,
    useGetDocuments,
    useGetDocumentsScanning,
    useMoveDocumentsToTrash,
    usePermanentlyDeleteDocuments,
    useUpdateDocument,
    useUpdateDocumentCategories,
    useUpdateDocumentFileName,
    useUpdateDocumentLabels,
    useMarkDocumentAsSeen,
    useCopyInheritedDocument,
    useCopyDocument,
    useUnShareDocuments,
    useShareDocuments,
    useUnTrashDocuments,
    useAcceptRejectSharedDocuments,
    useDocumentLabels,
    useTrashDocumentIds,
    useCopyMultipleInheritedDocuments,
    useEmptyTrash,
    useDocumentExtendTrashDate,
  };
};

export default createDocumentsModule;
